import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Icon,
  useBreakpointValue
} from '@chakra-ui/react';
import { CheckIcon, CloseIcon } from '@chakra-ui/icons';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);

/**
 * Course Audience Check Section
 * Who is this course for / Who should NOT take this
 */
const CourseAudienceCheck = ({ data }) => {
  const gridCols = useBreakpointValue({ base: 1, md: 2 });

  if (!data) return null;

  const {
    title = "Who Is This Course For?",
    subtitle,
    idealFor = [],
    notIdealFor = []
  } = data;

  const hasContent = idealFor.length > 0 || notIdealFor.length > 0;
  if (!hasContent) return null;

  return (
    <Box py={{ base: 16, md: 20 }} bg="gray.50" borderY="1px solid" borderColor="gray.200">
      <Container maxW="container.lg">
        {/* Header */}
        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          mb={{ base: 12, md: 16 }}
        >
          <VStack align="center" spacing={3} textAlign="center">
            <Heading as="h2" size="2xl" fontWeight="900" color="gray.900">
              {title}
            </Heading>
            {subtitle && (
              <Text fontSize="lg" color="gray.600" maxW="2xl">
                {subtitle}
              </Text>
            )}
          </VStack>
        </MotionBox>

        {/* Two Column Layout */}
        <SimpleGrid columns={gridCols} spacing={{ base: 8, md: 12 }}>
          {/* Ideal For */}
          {idealFor.length > 0 && (
            <MotionBox
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <VStack align="start" spacing={4}>
                <Heading as="h3" size="md" color="green.700" display="flex" alignItems="center">
                  <Icon as={CheckIcon} mr={2} color="green.600" />
                  Ideal For
                </Heading>
                <VStack align="start" spacing={3} w="full">
                  {idealFor.map((item, idx) => (
                    <HStack key={idx} align="start" spacing={3}>
                      <Icon as={CheckIcon} color="green.600" mt="4px" flexShrink={0} />
                      <Text color="gray.700">{item}</Text>
                    </HStack>
                  ))}
                </VStack>
              </VStack>
            </MotionBox>
          )}

          {/* Not Ideal For */}
          {notIdealFor.length > 0 && (
            <MotionBox
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <VStack align="start" spacing={4}>
                <Heading as="h3" size="md" color="red.700" display="flex" alignItems="center">
                  <Icon as={CloseIcon} mr={2} color="red.600" />
                  Not Ideal For
                </Heading>
                <VStack align="start" spacing={3} w="full">
                  {notIdealFor.map((item, idx) => (
                    <HStack key={idx} align="start" spacing={3}>
                      <Icon as={CloseIcon} color="red.600" mt="4px" flexShrink={0} />
                      <Text color="gray.700">{item}</Text>
                    </HStack>
                  ))}
                </VStack>
              </VStack>
            </MotionBox>
          )}
        </SimpleGrid>
      </Container>
    </Box>
  );
};

export default CourseAudienceCheck;
